import React, { useEffect, useState } from "react";
import { Col, Container, Form, Row, Table } from "react-bootstrap";
import { useLoaderData } from "react-router";
import { getUmpireRatings } from "../../lib/api";
import { IUmpireRating } from "../../types/api";
import Select from "react-select";
import { CSVLink } from "react-csv";
import { store } from "../..";
import {
    globalActions,
    selectSetting,
} from "../../features/global/globalSlice";
import { useAppSelector } from "../../hooks/reduxHooks";
import { capitalize, isXOrSmaller } from "../../lib/utils";
import useBreakpoint from "../../hooks/useBreakpoint";
import {
    leagueManagementActions,
    selectYears,
} from "../../features/leaguemgmt/leagueManagementSlice";

interface Filters {
    umpire: string | undefined;
    effort: number | undefined;
    attitude: number | undefined;
    consistency: number | undefined;
    overall: number | undefined;
    games: number | undefined;
    ageLevels: {
        options: Array<string>;
        filter: Array<string> | undefined;
    };
    year: {
        options: Array<number>;
        filter: number | undefined;
    };
}

interface CSVData {
    umpire: string;
    games: number;
    overall: string;
    consistency: string;
    attitude: string;
    effort: string;
}

type SortOrderType = Partial<
    Record<keyof IUmpireRating["ratings"] | "umpire" | "games", "asc" | "desc">
>;

export const loader = async () => {
    const settings = store.getState().global.settings;
    const ageLevels = store.getState().global.ageLevels;
    const { years, leagues } = store.getState().leaguemanagement;

    const promises = [];

    !years && promises.push(store.dispatch(leagueManagementActions.getYears()));
    !leagues.length &&
        promises.push(store.dispatch(leagueManagementActions.getLeagues()));
    !ageLevels.length &&
        promises.push(store.dispatch(globalActions.getAgeLevels()));
    !settings.length &&
        promises.push(store.dispatch(globalActions.getSettings()));

    const [{ ratings }] = await Promise.all([getUmpireRatings(), ...promises]);

    return {
        ratings,
    };
};

const UmpireRatings = (): React.JSX.Element => {
    const { ratings: loadedRatings } = useLoaderData() as {
        ratings: IUmpireRating[];
    };
    const breakpoint = useBreakpoint();

    const ageLevels = useAppSelector(state => state.global.ageLevels);
    const years = useAppSelector(selectYears);
    const currentYear = useAppSelector(state =>
        selectSetting(state, "majorybl_current_league_year"),
    );
    const [ratings, setRatings] = useState<IUmpireRating[]>(loadedRatings);

    const [csvData, setCSVData] = useState<Array<CSVData>>([]);
    const [sortOrder, setSortOrder] = useState<SortOrderType>({
        umpire: "asc",
    });

    const ratingOptions = [
        {
            value: 5,
            label: 5,
        },
        {
            value: 4,
            label: 4,
        },
        {
            value: 3,
            label: 3,
        },
        {
            value: 2,
            label: 2,
        },
        {
            value: 1,
            label: 1,
        },
    ];

    const calculateRating = (type: string, ratings: IUmpireRating[]) => {
        const total = ratings.reduce(
            (acc, r) => acc + (r.ratings[type as keyof typeof r.ratings] || 0),
            0,
        );

        const count = ratings.reduce(
            (acc, r) =>
                acc +
                (r.ratings[type as keyof typeof r.ratings] !== undefined &&
                r.ratings[type as keyof typeof r.ratings] !== null
                    ? 1
                    : 0),
            0,
        );

        return count > 0 ? (total / count).toFixed(3) : "N/A";
    };

    const [umpires, setUmpires] = useState(
        Array.from(new Set(ratings.map(r => parseInt(r.umpire.id.toString()))))
            .map(u => {
                const umpireRatings = ratings.filter(r => r.umpire.id === u);

                return {
                    umpire: ratings.find(
                        r => parseInt(r.umpire.id.toString()) === u,
                    )!.umpire,
                    ratings: ratings.filter(r => r.umpire.id === u),
                    effort: calculateRating("effort", umpireRatings),
                    attitude: calculateRating("attitude", umpireRatings),
                    consistency: calculateRating("consistency", umpireRatings),
                    overall: calculateRating("overall", umpireRatings),
                };
            })
            .sort((a, b) => {
                return a.umpire?.name.localeCompare(b.umpire?.name);
            }),
    );

    const [filters, setFilters] = useState<Filters>({
        umpire: undefined,
        effort: undefined,
        attitude: undefined,
        consistency: undefined,
        overall: undefined,
        games: undefined,
        ageLevels: {
            options: ageLevels.map(a => a.ageLevel),
            filter: undefined,
        },
        year: {
            options: years
                ? Array.from(Object.keys(years)).map(y => parseInt(y))
                : [],
            filter: parseInt(currentYear?.setting) || undefined,
        },
    });

    const checkRatingsFilters = (ratings: {
        attitude: string;
        effort: string;
        consistency: string;
        overall: string;
    }): boolean => {
        let returnValue = true;
        for (let key of Object.keys(ratings) as (keyof typeof ratings)[]) {
            if (filters[key]) {
                // Check if the rating is "N/A" or below the filter
                if (ratings[key] === "N/A") {
                    returnValue = false;
                } else {
                    returnValue = parseFloat(ratings[key]) <= filters[key]!;
                }
            }
        }

        return returnValue;
    };

    const CSVHeaders = [
        { label: "Umpire", key: "umpire" },
        { label: "Number of Games", key: "games" },
        { label: "Overall Rating", key: "overall" },
        { label: "Consistency Rating", key: "consistency" },
        { label: "Attitude Rating", key: "attitude" },
        { label: "Effort Rating", key: "effort" },
    ];

    const handleDownload = () => {
        const filteredData = umpires
            .map(u => {
                return {
                    umpire: u.umpire.name,
                    games: u.ratings.length,
                    overall: u.overall,
                    consistency: u.consistency,
                    attitude: u.attitude,
                    effort: u.effort,
                };
            })
            .filter((data): data is CSVData => data !== null);

        setCSVData(filteredData);
    };

    const downArrow = (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 25">
            <path d="m18.294 16.793-5.293 5.293V1h-1v21.086l-5.295-5.294-.707.707L12.501 24l6.5-6.5-.707-.707z" />
        </svg>
    );

    const upArrow = (
        <svg
            data-name="1-Arrow Up"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 32 32"
        >
            <path d="m26.71 10.29-10-10a1 1 0 0 0-1.41 0l-10 10 1.41 1.41L15 3.41V32h2V3.41l8.29 8.29z" />
        </svg>
    );

    const handleSortClick = (
        key: keyof IUmpireRating["ratings"] | "umpire" | "games",
    ) => {
        setSortOrder(prevSortOrder => {
            const newSortOrder: SortOrderType = {};

            if (prevSortOrder) {
                if (prevSortOrder[key] === "asc") {
                    newSortOrder[key] = "desc";
                } else if (prevSortOrder[key] === "desc") {
                    newSortOrder[key] = undefined;
                } else {
                    newSortOrder[key] = "asc";
                }
            } else {
                newSortOrder[key] = "asc";
            }

            return newSortOrder;
        });
    };

    useEffect(() => {
        const newUmpires = Array.from(
            new Set(ratings.map(r => parseInt(r.umpire.id.toString()))),
        )
            .map(u => {
                return {
                    umpire: ratings.find(
                        r => parseInt(r.umpire.id.toString()) === u,
                    )!.umpire,
                    ratings: ratings
                        .filter(r => r.umpire.id === u)
                        .filter(r =>
                            filters.ageLevels.filter
                                ? filters.ageLevels.filter?.includes(
                                      r.game.ageLevel.ageLevel,
                                  )
                                : true,
                        ),
                    effort: 0,
                    attitude: 0,
                    consistency: 0,
                    overall: 0,
                };
            })
            .filter(u =>
                filters.umpire
                    ? u.umpire.name
                          .toLowerCase()
                          .includes(filters.umpire.toLowerCase())
                    : true,
            )
            .filter(u =>
                filters.ageLevels.filter
                    ? u.ratings.some(r =>
                          filters.ageLevels.filter?.includes(
                              r.game.ageLevel.ageLevel,
                          ),
                      )
                    : true,
            )
            .filter(u =>
                filters.games ? u.ratings.length >= filters.games : true,
            )
            .map(u => {
                return {
                    umpire: u.umpire,
                    ratings: u.ratings,
                    effort: calculateRating("effort", u.ratings),
                    attitude: calculateRating("attitude", u.ratings),
                    consistency: calculateRating("consistency", u.ratings),
                    overall: calculateRating("overall", u.ratings),
                };
            });

        setUmpires(newUmpires);
    }, [filters, ratings]);

    const handleYearChange = async (year: number | undefined) => {
        const { ratings } = await getUmpireRatings(undefined, year);
        setRatings(ratings);
    };

    return (
        <div data-testid="ratingsContent" className="ratingsContent">
            <Container>
                <Row className="ratingsFilters">
                    <Col xs={12} sm={6} md={4} className="my-2">
                        <h5>Umpire:</h5>
                        <Form.Control
                            type="text"
                            placeholder="Umpire Name"
                            onChange={e => {
                                setFilters(prev => ({
                                    ...prev,
                                    umpire:
                                        e.target.value.trim() === ""
                                            ? undefined
                                            : e.target.value.trim(),
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={3} className="my-2">
                        <h5>Age Level:</h5>
                        <Select
                            isSearchable={false}
                            isClearable={true}
                            isMulti={true}
                            options={filters.ageLevels.options.map(a => {
                                return {
                                    value: a,
                                    label: a,
                                };
                            })}
                            onChange={a => {
                                setFilters(prev => ({
                                    ...prev,
                                    ageLevels: {
                                        ...prev.ageLevels,
                                        filter:
                                            a.length === 0
                                                ? undefined
                                                : a?.map(o => o.label),
                                    },
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={3} className="my-2">
                        <h5>
                            {isXOrSmaller("lg", breakpoint)
                                ? "Min."
                                : "Minimum"}{" "}
                            Number of Ratings
                        </h5>
                        <Form.Control
                            name="games"
                            onChange={e => {
                                const value = e.currentTarget
                                    ? e.currentTarget.value
                                    : "";
                                setFilters(prev => ({
                                    ...prev,
                                    games: value ? parseInt(value) : undefined,
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={2} className="my-2">
                        <h5>Year:</h5>
                        <Select
                            isSearchable={false}
                            isClearable={true}
                            isMulti={false}
                            options={filters.year.options.map(y => {
                                return {
                                    value: y,
                                    label: y,
                                };
                            })}
                            defaultValue={
                                filters.year.filter
                                    ? {
                                          value: filters.year.filter,
                                          label: filters.year.filter,
                                      }
                                    : undefined
                            }
                            onChange={y => {
                                setFilters(prev => ({
                                    ...prev,
                                    year: {
                                        ...prev.year,
                                        filter: y?.value,
                                    },
                                }));
                                handleYearChange(y?.value);
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={3} className="my-2">
                        <h5>Overall</h5>
                        <Select
                            isSearchable={false}
                            isClearable={true}
                            isMulti={false}
                            options={ratingOptions}
                            onChange={a => {
                                setFilters(prev => ({
                                    ...prev,
                                    overall: a?.value,
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={3} className="my-2">
                        <h5>Consistency</h5>
                        <Select
                            isSearchable={false}
                            isClearable={true}
                            isMulti={false}
                            options={ratingOptions}
                            onChange={a => {
                                setFilters(prev => ({
                                    ...prev,
                                    consistency: a?.value,
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={3} className="my-2">
                        <h5>Attitude</h5>
                        <Select
                            isSearchable={false}
                            isClearable={true}
                            isMulti={false}
                            options={ratingOptions}
                            onChange={a => {
                                setFilters(prev => ({
                                    ...prev,
                                    attitude: a?.value,
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={3} className="my-2">
                        <h5>Effort</h5>
                        <Select
                            isSearchable={false}
                            isClearable={true}
                            isMulti={false}
                            options={ratingOptions}
                            onChange={a => {
                                setFilters(prev => ({
                                    ...prev,
                                    effort: a?.value,
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={12}>
                        <p style={{ marginBottom: 0, marginTop: ".5em" }}>
                            Ratings filters will filter by max rating value (ie.
                            3 will filter any ratings up to a max of 3)
                        </p>
                    </Col>
                </Row>

                <Row>
                    <Col className="d-flex justify-content-end">
                        <CSVLink
                            data={csvData}
                            headers={CSVHeaders}
                            filename={`umpire-ratings.${new Date().getTime()}.csv`}
                            className="btn btn-primary pull-right"
                            target="_blank"
                            onClick={handleDownload}
                        >
                            Export Ratings Data
                        </CSVLink>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Table
                            className="currentYearRatings umpire"
                            striped
                            responsive
                        >
                            <thead>
                                <tr>
                                    {(
                                        [
                                            "umpire",
                                            "games",
                                            "overall",
                                            "consistency",
                                            "attitude",
                                            "effort",
                                        ] as (keyof SortOrderType)[]
                                    ).map(c => (
                                        <th key={c}>
                                            <span
                                                onClick={() =>
                                                    handleSortClick(c)
                                                }
                                            >
                                                {c === "games"
                                                    ? "Number of Ratings"
                                                    : capitalize(c)}{" "}
                                                {sortOrder?.[c] === "asc"
                                                    ? upArrow
                                                    : sortOrder?.[c] === "desc"
                                                      ? downArrow
                                                      : ""}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {umpires
                                    .sort((a, b) => {
                                        if (sortOrder) {
                                            const sortKey = Object.keys(
                                                sortOrder,
                                            )[0] as keyof SortOrderType;

                                            if (sortKey === "games") {
                                                return sortOrder[sortKey] ===
                                                    "asc"
                                                    ? a.ratings.length -
                                                          b.ratings.length
                                                    : b.ratings.length -
                                                          a.ratings.length;
                                            } else {
                                                let aValue = a[sortKey];
                                                let bValue = b[sortKey];

                                                const parsedAValue =
                                                    typeof aValue === "string"
                                                        ? parseFloat(aValue)
                                                        : aValue;
                                                const parsedBValue =
                                                    typeof bValue === "string"
                                                        ? parseFloat(bValue)
                                                        : bValue;

                                                if (
                                                    sortKey === "umpire" &&
                                                    typeof aValue ===
                                                        "object" &&
                                                    typeof bValue === "object"
                                                ) {
                                                    return sortOrder[
                                                        sortKey
                                                    ] === "desc"
                                                        ? bValue.name.localeCompare(
                                                              aValue.name,
                                                          )
                                                        : aValue.name.localeCompare(
                                                              bValue.name,
                                                          );
                                                } else {
                                                    if (
                                                        sortOrder[sortKey] ===
                                                        "asc"
                                                    ) {
                                                        if (
                                                            parsedAValue <
                                                            parsedBValue
                                                        )
                                                            return -1;
                                                        if (
                                                            parsedAValue >
                                                            parsedBValue
                                                        )
                                                            return 1;
                                                        return 0;
                                                    } else if (
                                                        sortOrder[sortKey] ===
                                                        "desc"
                                                    ) {
                                                        if (
                                                            parsedAValue >
                                                            parsedBValue
                                                        )
                                                            return -1;
                                                        if (
                                                            parsedAValue <
                                                            parsedBValue
                                                        )
                                                            return 1;
                                                        return 0;
                                                    }

                                                    return 0;
                                                }
                                            }
                                        } else {
                                            return 0;
                                        }
                                    })
                                    .map(u => {
                                        return checkRatingsFilters({
                                            effort: u.effort,
                                            attitude: u.attitude,
                                            consistency: u.consistency,
                                            overall: u.overall,
                                        }) ? (
                                            <tr key={u.umpire.id}>
                                                <td>{u.umpire.name}</td>
                                                <td>{u.ratings.length}</td>
                                                <td>{u.overall}</td>
                                                <td>{u.consistency}</td>
                                                <td>{u.attitude}</td>
                                                <td>{u.effort}</td>
                                            </tr>
                                        ) : null;
                                    })}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default UmpireRatings;
